import axios from 'axios';
import Task from '@/classes/task/Task';
import { TaskResponse } from '@/classes/task/types';
import { HttpMethod } from '@/types/HttpMethods';
import { pretension, pretensionsReport, diadocAuth } from '@/utils/config';
import {
  CreatePretensionData, UpdatePretensionData, PretensionReportParams, SaveDraftData, ListDraftsOptions,
} from './types';
import { StartSigningRequest } from './PretensionSigning.types';

interface PretensionListOptions {
  suppliers?: Array<string>;
  createdAt?: Array<string>;
  platformId?: string;
  type?: string;
  status?: string;
  // Задача 10833: претензии, в строках которых есть хоть один из этих листов учёта.
  sheetIds?: Array<number>;
  pageFrom: number;
  pageSize: number;
}

class PretensionController {
  public static async update(data: UpdatePretensionData): Promise<TaskResponse> {
    const url = pretension.update;
    const result = await Task.execute({ url, method: HttpMethod.PUT, body: data });
    return result;
  }

  public static async create(data: CreatePretensionData): Promise<TaskResponse> {
    const url = pretension.create;
    const result = await Task.execute({ url, method: HttpMethod.POST, body: data });
    return result;
  }

  // markSeen: карточка на экране — коннектор отмечает претензию прочитанной (задача 10833).
  public static async single(id: number, markSeen = false): Promise<TaskResponse> {
    const url = `${pretension.single}?id=${id}${markSeen ? '&markSeen=1' : ''}`;
    const result = await Task.execute({ url, method: HttpMethod.GET });
    return result;
  }

  /**
   * BC3.3 followup (rev.4/5): sync URL builder для /pretension-document-link.
   * Backend стримит file content через GET с query-params:
   *   - pretensionId, documentType — payload
   *   - objectName — уникальный ключ файла (BUG-6): дискриминатор открытия конкретного
   *     вложения. Несколько документов одного type ('attachment') различаются ТОЛЬКО им.
   *     documentType оставлен для обратной совместимости / fallback на бэке для старых ссылок.
   * Токен в ссылку не кладём: шлюз берёт его из куки documentLinkTokenCookie.
   * Возвращает stable URL для use в <a href> (browser native navigation).
   * Никакого async / TaskResponse — link встраивается в template напрямую.
   */
  public static documentLink(pretensionId: number, documentType: string, objectName?: string): string {
    const qs = new URLSearchParams({
      pretensionId: String(pretensionId),
      documentType,
    });
    // objectName опционален в сигнатуре (defensive), но фронт всегда его передаёт.
    if (objectName) qs.set('objectName', objectName);
    return `${pretension.documentLink}?${qs.toString()}`;
  }

  public static async list(options: PretensionListOptions): Promise<TaskResponse> {
    let url = pretension.list;
    const query: Array<{ key: string; value: string | null | undefined }> = [];
    query.push({ key: 'suppliers', value: options.suppliers?.join(',') });
    query.push({ key: 'createdAt', value: options.createdAt?.join(',') });
    query.push({ key: 'platformId', value: options.platformId });
    query.push({ key: 'type', value: options.type });
    query.push({ key: 'status', value: options.status });
    query.push({ key: 'sheetIds', value: options.sheetIds?.join(',') });
    query.push({ key: 'pageFrom', value: options.pageFrom?.toString() });
    query.push({ key: 'pageSize', value: options.pageSize?.toString() });
    const queryString = query.filter((v: any) => !!v.value).map((v: any) => `${v.key}=${v.value}`).join('&');
    if (queryString) {
      url += `?${queryString}`;
    }
    const result = await Task.execute({ url, method: HttpMethod.GET });
    return result;
  }

  public static async report(params: PretensionReportParams): Promise<TaskResponse> {
    const url = pretensionsReport.getData;
    const result = await Task.execute({ url, method: HttpMethod.POST, body: params });
    return result;
  }

  /**
   * @deprecated T4 (FML-wsj2.3) — используйте startSigning(). Удалить после
   * успешной приёмки T3 (BPMN-kjar) на проде. До приёмки оставлен как fallback.
   */
  public static async approveBySigner(
    pretensionId: number,
    signerEmail: string,
    diadocSignerLogin: string,
    diadocSignerPassword: string,
  ): Promise<TaskResponse> {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[deprecated] PretensionController.approveBySigner: use startSigning (T4)');
    }
    const url = pretension.status.processSigner;
    const payload = {
      pretensionId,
      signerEmail,
      diadocSignerLogin,
      diadocSignerPassword,
    };
    const result = await Task.execute({ url, method: HttpMethod.PUT, body: payload });
    return result;
  }

  /**
   * T4 (FML-wsj2.3): начало подписания через PHP `/pretension-start-signing`.
   * Direct route, минующий Talend. Использует raw axios (НЕ Task.execute),
   * потому что Task.execute теряет error.response.data на не-2xx (R-T4-11):
   * 409-конфликт несёт обязательный JSON-payload {error:{data:{reason,...}}}.
   * Контракт: pretensions-signing-php-spec.md §5.
   */
  public static async startSigning(payload: StartSigningRequest): Promise<TaskResponse> {
    try {
      const response = await axios.post(pretension.startSigning, payload);
      return { code: response.status, result: [null, response.data] };
    } catch (error) {
      const status = error?.response?.status ?? 500;
      // Если PHP вернул JSON body — кладём его (preserves {error:{data:{reason}}}).
      // Иначе (network/timeout) — error.message-строка как fallback.
      const body = error?.response?.data ?? (error?.message || error);
      return { code: status, result: [body, null] };
    }
  }

  /**
   * Diadoc OIDC (FML-5vmy, Часть 1): проверка статуса сессии подписанта.
   * Идемпотентное чтение → GET через Talend-ESB (vue → Talend → diadoc-api).
   * Тонкий wrapper: разворот Talend-обёртки и fail-closed трактовку valid делает
   * сервис signerSession.ts при чтении (единая HTTP-поверхность остаётся здесь).
   * Контракт ответа джобы: { valid: boolean, expiresAt: number | null } (epoch ms, канон [F1]).
   */
  public static async checkSignerSession(signerEmail: string): Promise<TaskResponse> {
    const url = `${diadocAuth.checkSignerSession}?signerEmail=${encodeURIComponent(signerEmail)}`;
    return Task.execute({ url, method: HttpMethod.GET });
  }

  /** 11991: отмена с обязательной причиной (3–2000 символов) — PHP через шлюз session_me. */
  public static async cancel(id: number, comment: string): Promise<TaskResponse> {
    return PretensionController.unwrapPhp(
      await Task.execute({ url: pretension.cancel, method: HttpMethod.POST, body: { id, comment } }),
    );
  }

  public static async paymentDone(pretensionId: number): Promise<TaskResponse> {
    const url = pretension.status.paymentDone;
    const result = await Task.execute({ url, method: HttpMethod.PUT, body: { pretensionId } });
    return result;
  }

  public static async approveByControlling(pretensionId: number): Promise<TaskResponse> {
    const url = pretension.status.processControlling;
    return Task.execute({ url, method: HttpMethod.PUT, body: { pretensionId, action: 'approve' } });
  }

  public static async controlReject(pretensionId: number, comment: string): Promise<TaskResponse> {
    const url = pretension.status.processControlling;
    return Task.execute({ url, method: HttpMethod.PUT, body: { pretensionId, action: 'reject', comment } });
  }

  public static async controlResubmit(pretensionId: number): Promise<TaskResponse> {
    const url = pretension.status.controlResubmit;
    return Task.execute({ url, method: HttpMethod.PUT, body: { pretensionId } });
  }

  /** §6.4 resume из problem → этап подписи. Сигнал pretension_resume_to_signing (frozen §11.1). */
  public static async resumeToSigning(pretensionId: number): Promise<TaskResponse> {
    return Task.execute({ url: pretension.status.resumeSigning, method: HttpMethod.PUT, body: { pretensionId } });
  }

  /** §6.4 resume из problem → этап доставки поставщику. Сигнал pretension_resume_to_supplier. */
  public static async resumeToSupplier(pretensionId: number): Promise<TaskResponse> {
    return Task.execute({ url: pretension.status.resumeSupplier, method: HttpMethod.PUT, body: { pretensionId } });
  }

  /** §6.4 resume из problem → этап бухгалтерии. Сигнал pretension_resume_to_accounting. */
  public static async resumeToAccounting(pretensionId: number): Promise<TaskResponse> {
    return Task.execute({ url: pretension.status.resumeAccounting, method: HttpMethod.PUT, body: { pretensionId } });
  }

  /** §8 явная публикация в ERP (in-place, sent_to_erp_at). Сигнал pretension_accountant_send_to_erp. */
  public static async sendToErp(pretensionId: number): Promise<TaskResponse> {
    return Task.execute({ url: pretension.status.sendToErp, method: HttpMethod.PUT, body: { pretensionId } });
  }

  /**
   * Прямые PHP-роуты отдают сырую обёртку {statusCode, data} (Talend её разворачивает,
   * прямой PHP — нет). Разворачиваем .data в result[1] (паттерн SigningModal result?.data ?? result).
   * На не-2xx Task.execute кладёт message в result[0], result[1]=null — оставляем как есть.
   */
  private static unwrapPhp(resp: TaskResponse): TaskResponse {
    const [error, data] = resp.result;
    if (error == null && data != null && typeof data === 'object' && 'data' in data) {
      return { code: resp.code, result: [null, (data as { data: unknown }).data] };
    }
    return resp;
  }

  public static async saveDraft(body: SaveDraftData): Promise<TaskResponse> {
    return PretensionController.unwrapPhp(
      await Task.execute({ url: pretension.draft.save, method: HttpMethod.POST, body }),
    );
  }

  public static async updateDraft(id: number, body: SaveDraftData, ifMatch: string): Promise<TaskResponse> {
    return PretensionController.unwrapPhp(await Task.execute({
      url: pretension.draft.update, method: HttpMethod.PUT, body: { ...body, id }, headers: { 'If-Match': ifMatch },
    }));
  }

  /**
   * DELETE с телом {id} (PHP читает id из body) + If-Match. Task.execute не шлёт тело на
   * DELETE — raw axios (config.data). cleanupAttachments не нужен: под Вариантом B delete
   * всегда abandon, бэк чистит S3 по умолчанию.
   */
  public static async deleteDraft(id: number, ifMatch: string): Promise<TaskResponse> {
    try {
      const response = await axios.delete(pretension.draft.delete, { data: { id }, headers: { 'If-Match': ifMatch } });
      const payload = response.data?.data ?? response.data;
      return { code: response.status, result: [null, payload] };
    } catch (error) {
      const status = error?.response?.status ?? 500;
      return { code: status, result: [error?.message || error, null] };
    }
  }

  public static async listDrafts(options: ListDraftsOptions): Promise<TaskResponse> {
    return PretensionController.unwrapPhp(
      await Task.execute({ url: pretension.draft.list, method: HttpMethod.POST, body: options }),
    );
  }

  public static async getDraft(id: number): Promise<TaskResponse> {
    return PretensionController.unwrapPhp(
      await Task.execute({ url: pretension.draft.get, method: HttpMethod.POST, body: { id } }),
    );
  }

  /**
   * п.4: сброс problem→draft. АСИНХРОНЕН — 200 = «сигнал принят» (status в ответе ещё problem);
   * запись status=draft делает BPMN позже. Не трактовать ответ как «уже черновик».
   */
  public static async resetToDraft(id: number): Promise<TaskResponse> {
    return PretensionController.unwrapPhp(
      await Task.execute({ url: pretension.draft.resetToDraft, method: HttpMethod.POST, body: { id } }),
    );
  }

  /**
   * Preview PDF — через Talend (нужно обогащение справочниками). Raw axios + responseType:'blob'.
   * Authorization навешивает глобальный axios-интерсептор.
   */
  public static async previewPdf(payload: CreatePretensionData): Promise<TaskResponse> {
    try {
      const response = await axios.post(pretension.draft.preview, payload, { responseType: 'blob' });
      return { code: response.status, result: [null, response.data] };
    } catch (error) {
      const status = error?.response?.status ?? 500;
      return { code: status, result: [error?.message || error, null] };
    }
  }
}

export default PretensionController;
